import React from 'react';
import I18n from 'i18n-js';
import * as elog from '@reverbdotcom/commons/src/elog';
import { formatBid, DELETE_BID_AMOUNT } from './bump_helpers';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { CoreBumpRecommendationListingCard } from '@reverbdotcom/commons/src/gql/graphql';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { gql } from '@reverbdotcom/commons/src/gql';

const COMPONENT_NAME = 'BumpController';

export interface BumpControllerProps {
  listingId: string;
  bid: number;
  componentName?: string;
  showModal?: boolean;
  setShowModal?: (showModal) => void;
  hidePercentCallout?: boolean;
  buttonText?: string | React.ReactNode | JSX.Element;
  buttonClassName?: string;
  refetch?: (newBid: number | null) => void;
  onModalClose?: () => void;
  disabled?: boolean;
  minimumBumpBid?: number;
}

export function trackAdjustBumpRate(
  newBid: number,
  startingBid: number,
  bumpRecommendationAmount: number,
  listing: CoreBumpRecommendationListingCard.Fragment,
  componentName: string,
) {
  trackEvent({
    componentName,
    eventName: MParticleEventName.AdjustBumpRate,
    listing,
    startingBumpRatePercent: formatBid(startingBid),
    newBumpRatePercent: formatBid(newBid),
    bumpRecommendationPercent: bumpRecommendationAmount,
  });
}

const CREATE_PRODUCT_BUMP_RATE_MUTATION = gql(`
  mutation Core_CreateProductBumpRate($listingId: String!, $bid: Float!) {
    createProductBumpRate(input: { listingId: $listingId, bid: $bid }) {
      id
      rate
    }
  }
`);

const DELETE_PRODUCT_BUMP_RATE_MUTATION = gql(`
  mutation Core_DeleteProductBumpRate($listingId: String!) {
    deleteProductBumpRate(input: { listingId: $listingId }) {
      productBumpRates {
        id
        rate
      }
    }
  }
`);

export function bumpRecommendationsController(WrappedComponent) {
  return function BumpController(props: BumpControllerProps) {
    const [bid, setBid] = React.useState(props.bid);
    const [selectedBid, setSelectedBid] = React.useState(props.bid || 0);

    const [successMessage, setSuccessMessage] = React.useState('');
    const [failMessage, setFailMessage] = React.useState('');

    const [createProductBumpRateMutation] = useMutation(CREATE_PRODUCT_BUMP_RATE_MUTATION);
    const [deleteProductBumpRateMutation] = useMutation(DELETE_PRODUCT_BUMP_RATE_MUTATION);

    const { listingId } = props;

    React.useEffect(() => {
      setBid(props.bid);
      setSelectedBid(props.bid || 0);
    }, [props.bid]);

    const coreSetBid = async (
      newBid: number,
      bumpRecommendationAmount: number,
      listing: CoreBumpRecommendationListingCard.Fragment,
      refetch: (newBid: number | null) => void,
    ): Promise<void> => {
      const result = await createProductBumpRateMutation({
        variables: {
          listingId,
          bid: newBid,
        },
      });

      if (result.errors?.length > 0) {
        elog.error('Bump Create Bid Error', { bid: newBid }, result.errors.map((error) => error.message).join(', '));
        updateErrorState();
        return;
      }

      trackAdjustBumpRate(
        newBid,
        bid,
        bumpRecommendationAmount,
        listing,
        props.componentName || COMPONENT_NAME,
      );
      updateSuccessState(newBid, refetch);
    };

    const deleteBid = async (
      bumpRecommendationAmount: number,
      listing: CoreBumpRecommendationListingCard.Fragment,
      refetch: (newBid: number | null) => void,
    ): Promise<void> => {
      const result = await deleteProductBumpRateMutation({
        variables: {
          listingId,
        },
      });

      if (result.errors?.length > 0) {
        elog.error('Bump Delete Bid Error', {}, result.errors.map((error) => error.message).join(', '));
        updateErrorState();
        return;
      }

      trackAdjustBumpRate(
        DELETE_BID_AMOUNT,
        bid,
        bumpRecommendationAmount,
        listing,
        props.componentName || COMPONENT_NAME,
      );
      updateSuccessState(null, refetch);
    };

    const updateBid = async (
      newBid: number,
      bumpRecommendationAmount: number,
      listing: CoreBumpRecommendationListingCard.Fragment,
      refetch: (newBid: number | null) => void,
    ): Promise<void> => {
      if (newBid === DELETE_BID_AMOUNT) {
        await deleteBid(
          bumpRecommendationAmount,
          listing,
          refetch,
        );
      } else {
        await coreSetBid(
          newBid,
          bumpRecommendationAmount,
          listing,
          refetch,
        );
      }
    };

    const updateSuccessState = (
      newBid: (number | null),
      refetch: (newBid: number | null) => void,
    ) => {
      setBid(newBid);
      setSelectedBid(newBid);
      if (newBid === null) {
        setSuccessMessage(I18n.t('discovery.bump.successBidDelete'));
      } else {
        setSuccessMessage(I18n.t('discovery.bump.successBid', { percent: formatBid(newBid) }));
      }
      setFailMessage('');
      refetch(newBid);
    };

    const updateErrorState = () => {
      setSelectedBid(bid);
      setFailMessage(I18n.t('discovery.bump.errorBid'));
      setSuccessMessage('');
    };

    return (
      <WrappedComponent
        listingId={listingId}
        bid={bid}
        selectedBid={selectedBid}
        successMessage={successMessage}
        failMessage={failMessage}
        handleBidChange={setSelectedBid}
        updateBid={updateBid}
        showModal={props.showModal}
        setShowModal={props.setShowModal}
        hidePercentCallout={props.hidePercentCallout}
        buttonText={props.buttonText}
        buttonClassName={props.buttonClassName}
        refetch={props.refetch}
        onModalClose={props.onModalClose}
        disabled={props.disabled}
        minimumBumpBid={props.minimumBumpBid}
      />
    );
  };
}
